import { useMemo, useState } from "react";

import { PERMISSIONS_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { useTableSettings, useToast } from "@/ds";
import type { HubTableDataTableProps } from "@/ds";
import { useMediaQuery } from "@/hooks/use-media-query";
import type { Permission } from "@/modules/permissions";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { useTableSettingsAppliedToast } from "@/shared/hooks";
import { CONTROL_PANEL_MOBILE_QUERY } from "../control-panel.constants";
import { usePermissionsAtualizacao } from "./hooks/use-permissions-atualizacao";
import { usePermissionsExportacao } from "./hooks/use-permissions-exportacao";
import { usePermissionsLista } from "./hooks/use-permissions-lista";
import {
  PERMISSIONS_ALL_COLUMNS,
  PERMISSIONS_TABLE_LABEL,
  PERMISSIONS_TABLE_MIN_WIDTH,
} from "./permissions.constants";
import type { PermissionsColumnKey } from "./permissions.constants";
import type { PermissionLinha, PermissionsViewModel } from "./types-permissions";

/**
 * Aba Permissões do Painel (HU 898, 902 e a exportação da 1442): lista, edição e menu ⋮. A edição só existe com
 * `hub.permissao:editar` (ADR 0010).
 */
export function usePermissions(): PermissionsViewModel {
  const isMobile = useMediaQuery(CONTROL_PANEL_MOBILE_QUERY);
  const toast = useToast();
  const { hasPermissao } = usePermissoes();
  const podeEditar = hasPermissao(HUB_PERMISSOES.PERMISSAO_EDICAO);
  const [editando, setEditando] = useState<Permission | null>(null);

  const settings = useTableSettings<PermissionsColumnKey>({
    densityKey: PERMISSIONS_TABLE_DENSITY_KEY,
    allColumns: PERMISSIONS_ALL_COLUMNS,
  });
  const notifySettingsApplied = useTableSettingsAppliedToast(toast);
  const lista = usePermissionsLista();
  const exportView = usePermissionsExportacao({
    colunas: settings.visibleColumns,
    search: lista.busca,
    sort: lista.appliedSort,
    isEmpty: lista.status === "success" && lista.total === 0,
    toast,
  });
  const atualizar = usePermissionsAtualizacao({
    toast,
    onUpdated: () => {
      setEditando(null);
      lista.reload();
    },
  });

  const linhas = useMemo<PermissionLinha[]>(
    () =>
      lista.items.map((permission) => ({
        id: permission.id,
        permission,
        editLabel: `Editar permissão ${permission.nome}`,
        editTestId: `permissions-edit-${permission.id}`,
      })),
    [lista.items],
  );

  // O corpo rola dentro do cartão; abaixo da largura mínima a tabela rola na horizontal.
  const dataTableProps: HubTableDataTableProps = {
    scrollable: true,
    scrollHeight: "flex",
    tableStyle: { minWidth: PERMISSIONS_TABLE_MIN_WIDTH },
    "aria-label": PERMISSIONS_TABLE_LABEL,
  };

  return {
    isMobile,
    search: lista.search,
    buscar: lista.buscar,
    settings,
    exportView,
    notifySettingsApplied,
    items: linhas,
    total: lista.total,
    isFirstLoad: lista.status === "loading" && linhas.length === 0,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    table: lista.tabela,
    dataTableProps,
    hasSearch: lista.hasSearch,
    limparBusca: lista.clearSearch,
    podeEditar,
    editando,
    editar: podeEditar ? (linha) => setEditando(linha.permission) : undefined,
    fecharEdicao: () => setEditando(null),
    salvarEdicao: atualizar,
  };
}
